import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  HiOutlinePlus,
  HiOutlineTrash,
  HiExclamation,
  HiOutlineChevronUp,
  HiOutlineChevronDown,
  HiOutlineX,
  HiOutlineDotsVertical,
  HiOutlineInformationCircle,
  HiOutlineSparkles,
  HiOutlineRefresh,
  HiOutlineExclamation,
} from "react-icons/hi";
import { PiClockCounterClockwise } from "react-icons/pi";
import { toast } from "react-hot-toast";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import API from "../../api";
import Trigger from "../../components/question/Trigger";
import SyncConflictBanner from "../../components/SyncConflictBanner";
import DependencyExplorer from "../../components/dependencies/DependencyExplorer";
import RenditionOptions from "../../components/question/RenditionOptions";
import { validateTriggerGroups, INPUT_CLASS_NAME, newObjectId, needsVerification, CONFIG_STATUS, CONFIG_STATUS_LABEL, guidelineText } from "../../utils";
import { capture } from "../../instrument";
import { LuArrowRight, LuCopy, LuListTodo, LuMessageCircleQuestion, LuNetwork, LuSquareFunction, LuUnlink } from "react-icons/lu";
import SyncInfoBanner from "../../components/SyncInfoBanner";
import { useHotkeys } from "react-hotkeys-hook";
import useRecordNavShortcuts from "../../hooks/useRecordNavShortcuts";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";

const AUDIENCES = [
  { audience: "secretary", label: "Secrétaire", dotClass: "bg-purple-500", isCanonical: true },
  { audience: "patient", label: "Patient", dotClass: "bg-blue-500" },
  { audience: "ide", label: "IDE", dotClass: "bg-green-500" },
];

const QUESTION_TEXTAREA_CLASS =
  "w-full resize-y rounded-md border border-gray-200 bg-white px-3 py-2 text-sm leading-snug text-gray-900 placeholder:text-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500";

const EditQuestion = () => {
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const isDirtyRef = useRef(false);
  const markDirty = () => {
    isDirtyRef.current = true;
  };

  const filteredIds = location.state?.filteredIds || null;
  const currentIndex = useMemo(() => (filteredIds ? filteredIds.indexOf(id) : -1), [filteredIds, id]);
  const hasNavigation = filteredIds !== null && currentIndex !== -1;

  const preview = useMemo(() => {
    const cached = queryClient
      .getQueriesData({ queryKey: ["questions"] })
      .flatMap(([, questions]) => questions || [])
      .find((q) => q._id === id);
    if (!cached) return null;
    return { question: cached.question || "", questionCode: cached.questionCode };
  }, [id]);

  const navigateTo = (index) => {
    const targetId = filteredIds[index];
    history.replace({ pathname: `/question/${targetId}/edit`, state: { filteredIds } });
  };

  const navigatePrev = () => {
    if (!hasNavigation || currentIndex <= 0) return;
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    navigateTo(currentIndex - 1);
  };

  const navigateNext = () => {
    if (!hasNavigation || currentIndex >= filteredIds.length - 1) return;
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    navigateTo(currentIndex + 1);
  };

  const goBack = () => {
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    history.push("/question");
  };

  useRecordNavShortcuts(
    {
      onPrev: navigatePrev,
      onNext: navigateNext,
      onBack: goBack,
      canPrev: hasNavigation && currentIndex > 0,
      canNext: hasNavigation && currentIndex < filteredIds?.length - 1,
    },
    [hasNavigation, currentIndex, filteredIds],
  );

  const [question, setQuestion] = useState("");
  const [isRequired, setIsRequired] = useState(true);
  const [patient, setPatient] = useState(false);
  const [patientQuestion, setPatientQuestion] = useState("");
  const [patientIsRequired, setPatientIsRequired] = useState(true);
  const [ide, setIde] = useState(false);
  const [ideQuestion, setIdeQuestion] = useState("");
  const [ideIsRequired, setIdeIsRequired] = useState(true);
  const [canAnswerInPreRegistration, setCanAnswerInPreRegistration] = useState(false);
  const [questionCode, setQuestionCode] = useState("");
  const [answerType, setAnswerType] = useState("");
  const [groupsOperator, setGroupsOperator] = useState("AND");
  const [triggerGroups, setTriggerGroups] = useState([]);
  const [options, setOptions] = useState([]);
  const [linkedQuestions, setLinkedQuestions] = useState([]);
  const [linkedActions, setLinkedActions] = useState([]);
  const [linkedGuidelines, setLinkedGuidelines] = useState([]);
  const [skipIfAlreadyAnswered, setSkipIfAlreadyAnswered] = useState(false);
  const [autoAnswer, setAutoAnswer] = useState(false);
  const diagramOpen = true;

  const {
    data: questionData,
    isLoading: isLoadingData,
    isError,
  } = useQuery({
    queryKey: ["question", id],
    queryFn: async () => {
      const response = await API.get(`/question/${id}`);
      if (!response.ok) throw new Error(response.message || "Impossible de charger la question");
      return response.data;
    },
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!questionData) return;
    const opts = (questionData.options || []).map((o) => {
      const p = (questionData.patientOptions || []).find((x) => x.optionId?.toString() === o._id?.toString());
      const i = (questionData.ideOptions || []).find((x) => x.optionId?.toString() === o._id?.toString());
      return { _id: o._id, code: o.code, label: o.label || "", patientLabel: p?.label || "", patientHidden: !!p?.hidden, ideLabel: i?.label || "", ideHidden: !!i?.hidden };
    });
    setQuestion(questionData.question || "");
    setIsRequired(questionData.isRequired !== false);
    setPatient(!!questionData.patient);
    setPatientQuestion(questionData.patientQuestion || "");
    setPatientIsRequired(questionData.patientIsRequired !== false);
    setIde(!!questionData.ide);
    setIdeQuestion(questionData.ideQuestion || "");
    setIdeIsRequired(questionData.ideIsRequired !== false);
    setCanAnswerInPreRegistration(!!questionData.canAnswerInPreRegistration);
    setQuestionCode(questionData.questionCode);
    setAnswerType(questionData.answerType);
    setGroupsOperator(questionData.groupsOperator);
    setTriggerGroups(questionData.triggerGroups);
    setOptions(opts);
    setLinkedQuestions(questionData.linkedQuestions || []);
    setLinkedActions(questionData.linkedActions || []);
    setLinkedGuidelines(questionData.linkedGuidelines || []);
    setSkipIfAlreadyAnswered(questionData.skipIfAlreadyAnswered);
    setAutoAnswer(questionData.autoAnswer);
    isDirtyRef.current = false;
  }, [questionData]);

  useEffect(() => {
    if (!isError) return;
    toast.error("Une erreur est survenue lors du chargement de la question");
    history.push("/question");
  }, [isError]);

  const { data: diagramData } = useQuery({
    queryKey: ["diagramData"],
    queryFn: async () => {
      const [questions, guidelines, actions] = await Promise.all([API.get("/question"), API.get("/guideline"), API.get("/action")]);
      if (!questions.ok || !guidelines.ok || !actions.ok) throw new Error("Impossible de charger les dépendances");
      return { questions: questions.data, guidelines: guidelines.data, actions: actions.data };
    },
    enabled: diagramOpen,
  });


  const { data: sharingNetwork } = useQuery({
    queryKey: ["network-config"],
    queryFn: async () => {
      const response = await API.get("/network-config");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    staleTime: Infinity,
  });
  const isNetworkReplica = !!questionData?.networkSourceId;
  const isNetworkOwner = !!questionData?.sharedToNetwork;

  const linkedQuestionsInErrors = useMemo(() => {
    const linkedQuestionsResponse = linkedQuestions.flatMap((question) =>
      question.triggerGroups
        .flatMap((group) => group.triggers)
        .filter((trigger) => trigger.field === id)
        .flatMap((trigger) => trigger.values),
    );
    return linkedQuestionsResponse.some((response) => !options.find((op) => op._id === response));
  }, [linkedQuestions, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateMutation = useMutation({
    mutationFn: (body) => API.put(`/question/${id}`, body),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      isDirtyRef.current = false;

      queryClient.invalidateQueries({ queryKey: ["questions"] });
      queryClient.invalidateQueries({ queryKey: ["question", id] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });

      if (linkedQuestionsInErrors) {
        toast("Une question liée à cette question à un problème, veuillez vérifier", { icon: "⚠️" });
        return;
      }

      toast.success("Question modifiée avec succès");
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const { data: syncOverrides = [] } = useQuery({
    queryKey: ["sync-overrides", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const response = await API.get(`/sync/override?docId=${id}`);
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
  });
  const hasConflict = syncOverrides.some((override) => override.conflict);

  const verifyMutation = useMutation({
    mutationFn: () => API.post("/question/bulk", { ids: [id], action: "verify" }),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Question vérifiée");
      queryClient.invalidateQueries({ queryKey: ["questions"] });
      queryClient.invalidateQueries({ queryKey: ["question", id] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () => API.put(`/question/${id}`, { isDeleted: false }),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Question restaurée");
      queryClient.invalidateQueries({ queryKey: ["questions"] });
      queryClient.invalidateQueries({ queryKey: ["question", id] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => API.delete(`/question/${id}`),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Question supprimée avec succès");
      queryClient.invalidateQueries({ queryKey: ["questions"] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
      history.push("/question");
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue lors de la suppression");
    },
  });

  const shareMutation = useMutation({
    mutationFn: () => API.post("/network-config/question", { fromIds: [id] }),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      if (response.data?.errors?.length) return toast.error(response.data.errors[0].message);
      toast.success("Question partagée au réseau");
      queryClient.invalidateQueries({ queryKey: ["questions"] });
      queryClient.invalidateQueries({ queryKey: ["question", id] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const unshareMutation = useMutation({
    mutationFn: () => API.delete(`/network-config/question/${id}`),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Question retirée du réseau");
      queryClient.invalidateQueries({ queryKey: ["questions"] });
      queryClient.invalidateQueries({ queryKey: ["question", id] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const isMutating = updateMutation.isPending || deleteMutation.isPending;
  const isDeleted = !!questionData?.isDeleted;

  const hasOptions = answerType === "MULTIPLE_CHOICE" || answerType === "TEXT";
  const audienceOptionOverrides = (labelField, hiddenField) =>
    hasOptions
      ? options.flatMap((o) => {
          const label = o[labelField] && o[labelField] !== o.label ? o[labelField] : "";
          return label || o[hiddenField] ? [{ optionId: o._id, label, hidden: !!o[hiddenField] }] : [];
        })
      : [];

  const updateQuestion = () => {
    if (!question || !answerType || !questionCode || !triggerGroups?.length) return toast.error("Veuillez remplir tous les champs");

    if (linkedActions.length > 0 || linkedGuidelines.length > 0 || linkedQuestions.length > 0) {
      if (!window.confirm("Certains éléments sont liés à cette question. Voulez-vous vraiment la modifier ?")) return;
    }

    const error = validateTriggerGroups(triggerGroups);
    if (error) return toast.error(error);

    updateMutation.mutate({
      ...questionData,
      _id: id,
      question,
      isRequired,
      patient,
      patientQuestion: patient && patientQuestion && patientQuestion !== question ? patientQuestion : "",
      patientOptions: patient ? audienceOptionOverrides("patientLabel", "patientHidden") : [],
      patientIsRequired,
      ide,
      ideQuestion: ide && ideQuestion && ideQuestion !== question ? ideQuestion : "",
      ideOptions: ide ? audienceOptionOverrides("ideLabel", "ideHidden") : [],
      ideIsRequired,
      canAnswerInPreRegistration,
      questionCode,
      options: hasOptions ? options.map(({ _id, code, label }) => ({ _id, code, label })) : [],
      answerType,
      triggers: [],
      groupsOperator,
      triggerGroups,
      skipIfAlreadyAnswered,
      autoAnswer,
    });
  };

  useHotkeys(SHORTCUTS.EDIT_SAVE.keys, () => updateQuestion(), { preventDefault: true, enableOnFormTags: true, enabled: !isMutating && !questionData?.isDeleted }, [
    updateQuestion,
    isMutating,
    questionData?.isDeleted,
  ]);

  const deleteQuestion = () => {
    let confirmMessage = false;
    if (linkedActions.length > 0 || linkedGuidelines.length > 0 || linkedQuestions.length > 0) {
      confirmMessage = window.confirm("Certains éléments sont liés à cette question. Voulez-vous vraiment la supprimer ?");
    } else {
      confirmMessage = window.confirm("Êtes-vous sûr de vouloir supprimer cette question ?");
    }

    if (!confirmMessage) return;

    deleteMutation.mutate();
  };

  const duplicateQuestion = () => {
    const data = {
      question,
      isRequired,
      options: options.map(({ _id, code, label }) => ({ _id, code, label })),
      patient,
      patientQuestion,
      patientOptions: audienceOptionOverrides("patientLabel", "patientHidden"),
      patientIsRequired,
      ide,
      ideQuestion,
      ideOptions: audienceOptionOverrides("ideLabel", "ideHidden"),
      ideIsRequired,
      canAnswerInPreRegistration,
      questionCode: "",
      answerType,
      groupsOperator,
      triggerGroups: JSON.parse(JSON.stringify(triggerGroups)),
      skipIfAlreadyAnswered,
      autoAnswer,
    };
    history.push({ pathname: "/question/add", state: { duplicate: data } });
  };

  const onAddOption = () => {
    setOptions((prev) => [...prev, { _id: newObjectId(), code: "", label: "", patientLabel: "", patientHidden: false, ideLabel: "", ideHidden: false }]);
    markDirty();
  };

  const onRemoveOption = (optionId) => {
    setOptions((prev) => prev.filter((o) => o._id !== optionId));
    markDirty();
  };

  const onChangeOptionCode = (optionId, code) => {
    setOptions((prev) => prev.map((o) => (o._id === optionId ? { ...o, code } : o)));
    markDirty();
  };

  const onChangeOptionLabel = (audience, optionId, value) => {
    const field = audience === "secretary" ? "label" : audience === "patient" ? "patientLabel" : "ideLabel";
    setOptions((prev) => prev.map((o) => (o._id === optionId ? { ...o, [field]: value } : o)));
    markDirty();
  };

  const onToggleOptionDisabled = (audience, optionId) => {
    const field = audience === "patient" ? "patientHidden" : "ideHidden";
    setOptions((prev) => prev.map((o) => (o._id === optionId ? { ...o, [field]: !o[field] } : o)));
    markDirty();
  };

  const onReorderOptions = (orderedOptionIds) => {
    setOptions((prev) => orderedOptionIds.map((id) => prev.find((o) => o._id?.toString() === id?.toString())).filter(Boolean));
    markDirty();
  };

  const onChangeAudienceRequired = (audience, value) => {
    if (audience === "secretary") setIsRequired(value);
    else if (audience === "patient") setPatientIsRequired(value);
    else setIdeIsRequired(value);
    markDirty();
  };

  const audienceBadges = (q) => ["S", q.patient && "P", q.ide && "IDE"].filter(Boolean).join(" / ");

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      {hasNavigation && (
        <div className="flex items-center gap-1.5 border-b border-gray-200 bg-white px-3 py-1.5">
          <button type="button" className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600" onClick={goBack} title="Fermer (Esc)">
            <HiOutlineX className="h-4 w-4" />
          </button>
          <div className="mx-0.5 h-4 w-px bg-gray-200" />
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            onClick={navigatePrev}
            disabled={currentIndex <= 0}
          >
            <HiOutlineChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            onClick={navigateNext}
            disabled={currentIndex >= filteredIds.length - 1}
          >
            <HiOutlineChevronDown className="h-4 w-4" />
          </button>
          <span className="ml-1 text-xs tabular-nums text-gray-500">
            {currentIndex + 1} / {filteredIds.length}
          </span>
        </div>
      )}

      {isDeleted && (
        <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-100 px-8 py-2.5">
          <HiOutlineTrash className="h-4 w-4 shrink-0 text-gray-500" />
          <p className="text-sm text-gray-600">Cette question est supprimée : elle est affichée en lecture seule. Restaurez-la pour pouvoir la modifier.</p>
        </div>
      )}

      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="truncate text-lg font-semibold text-gray-900">{question || preview?.question || "Question"}</h1>
            {(questionData?.questionCode || preview?.questionCode) && (
              <span className="shrink-0 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">{questionData?.questionCode || preview?.questionCode}</span>
            )}
            {(isNetworkOwner || isNetworkReplica) && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-primary-200 bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
                <LuNetwork className="h-3 w-3" />
                {isNetworkOwner ? "Réseau · propriétaire" : "Réseau"}
              </span>
            )}
            {questionData && needsVerification(questionData) && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-orange-200 bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
                {questionData.configStatus === CONFIG_STATUS.CREATED ? <HiOutlineSparkles className="h-3 w-3" /> : <HiOutlineRefresh className="h-3 w-3" />}
                {CONFIG_STATUS_LABEL[questionData.configStatus]}
              </span>
            )}
            {hasConflict && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                <HiOutlineExclamation className="h-3 w-3" />
                Conflit
              </span>
            )}
            {isDeleted && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                <HiOutlineTrash className="h-3 w-3" />
                Supprimée
              </span>
            )}
            {!isDeleted && questionData?._sources?.some((s) => s.missingFromSource) && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                <HiOutlineTrash className="h-3 w-3" />
                Supprimé
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <div className="relative" ref={menuRef}>
            <button type="button" className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <HiOutlineDotsVertical className="h-5 w-5" />
            </button>
            {isMenuOpen && (
              <div className="absolute right-0 z-10 mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-lg">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-t-lg px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  onClick={() => {
                    setIsMenuOpen(false);
                    window.open(`/audit-log/questions/${id}`, "_blank", "noopener,noreferrer,width=1000,height=800");
                  }}
                >
                  <PiClockCounterClockwise className="h-4 w-4" />
                  Historique
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  onClick={() => {
                    setIsMenuOpen(false);
                    duplicateQuestion();
                  }}
                >
                  <LuCopy className="h-4 w-4" />
                  Dupliquer
                </button>
                {!isDeleted && sharingNetwork && !isNetworkOwner && !isNetworkReplica && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                    onClick={() => {
                      setIsMenuOpen(false);
                      if (isDirtyRef.current) return toast.error("Enregistrez vos modifications avant de partager");
                      if (
                        !window.confirm(
                          `Partager cette question au réseau ${sharingNetwork.name} ? Ce site en reste propriétaire : ses modifications et les mises à jour Kalisil s'appliqueront aux autres sites.`,
                        )
                      )
                        return;
                      shareMutation.mutate();
                    }}
                    disabled={shareMutation.isPending}
                  >
                    <LuNetwork className="h-4 w-4" />
                    Partager au réseau
                  </button>
                )}
                {!isDeleted && isNetworkOwner && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                    onClick={() => {
                      setIsMenuOpen(false);
                      if (isDirtyRef.current) return toast.error("Enregistrez vos modifications avant de dé-partager");
                      if (
                        !window.confirm(
                          "Ne plus partager cette question ? Elle redevient locale à ce site ; les autres sites la verront comme retirée du réseau et décideront de la conserver ou la supprimer.",
                        )
                      )
                        return;
                      unshareMutation.mutate();
                    }}
                    disabled={unshareMutation.isPending}
                  >
                    <LuUnlink className="h-4 w-4" />
                    Ne plus partager
                  </button>
                )}
                {!isDeleted && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-b-lg px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
                    onClick={() => {
                      setIsMenuOpen(false);
                      deleteQuestion();
                    }}
                    disabled={isMutating}
                  >
                    <HiOutlineTrash className="h-4 w-4" />
                    Supprimer
                  </button>
                )}
              </div>
            )}
          </div>
          {!isDeleted && questionData && needsVerification(questionData) && (
            <button
              type="button"
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
              onClick={() => {
                if (isDirtyRef.current) return toast.error("Enregistrez vos modifications avant de vérifier");
                verifyMutation.mutate();
              }}
              disabled={verifyMutation.isPending}
            >
              {verifyMutation.isPending ? "Vérification..." : "Marquer vérifié"}
            </button>
          )}
          {isDeleted ? (
            <button
              type="button"
              className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
              onClick={() => restoreMutation.mutate()}
              disabled={restoreMutation.isPending}
            >
              {restoreMutation.isPending ? "Restauration..." : "Restaurer"}
            </button>
          ) : (
            <button
              type="button"
              className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
              onClick={() => {
                updateQuestion();
                notifyShortcut("EDIT_SAVE");
              }}
              disabled={isMutating || isLoadingData}
            >
              {updateMutation.isPending ? "Enregistrement..." : "Enregistrer"}
            </button>
          )}
        </div>
      </div>

      {isLoadingData ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
          <span className="text-sm text-gray-400">Chargement...</span>
        </div>
      ) : (
        <div className="grid flex-1 grid-cols-5 overflow-hidden">
          <div className={`col-span-3 flex-1 overflow-y-auto ${isDeleted ? "pointer-events-none opacity-60" : ""}`} onChangeCapture={markDirty}>
            <fieldset disabled={isDeleted} className="mx-auto max-w-4xl px-8 py-6">
              {isNetworkOwner && (
                <div className="mb-6 flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
                  <LuNetwork className="mt-0.5 h-4 w-4 shrink-0 text-primary-500" />
                  <p className="text-sm text-primary-700">
                    Question partagée au réseau {sharingNetwork?.name || ""} — ce site est propriétaire : vos modifications et les mises à jour Kalisil s'appliquent aux{" "}
                    {sharingNetwork?.indexNames?.length || ""} sites du réseau.
                  </p>
                </div>
              )}
              {isNetworkReplica && (
                <div className="mb-6 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <LuNetwork className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                  <p className="text-sm text-gray-600">
                    Élément du réseau {sharingNetwork?.name || ""} — géré par un autre site. Vos modifications restent locales et seront signalées en conflit si le site
                    propriétaire modifie les mêmes champs.
                  </p>
                </div>
              )}
              <SyncConflictBanner docId={id} />
              <h3 className="mb-4 text-lg font-medium text-gray-900">Détails</h3>

              <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Type de réponse</label>
                  <select className={INPUT_CLASS_NAME} value={answerType} onChange={(e) => setAnswerType(e.target.value)}>
                    <option value="MULTIPLE_CHOICE">Choix multiple</option>
                    <option value="TEXT">Texte</option>
                    <option value="DATE">Date</option>
                    <option value="HOUR">Heure</option>
                    <option value="DOCUMENT">Document</option>
                  </select>
                </div>
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">{answerType === "DOCUMENT" ? "Nom du document" : "Code de la question"}</label>
                  <input
                    className={INPUT_CLASS_NAME}
                    placeholder={answerType === "DOCUMENT" ? "Nom du document" : "Code unique pour cette question"}
                    value={questionCode}
                    onChange={(e) => setQuestionCode(e.target.value)}
                    disabled={isMutating}
                  />
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <div className="flex items-baseline justify-between px-1">
                  <h4 className="text-sm font-medium text-gray-700">Versions par audience</h4>
                  <p className="text-xs text-gray-400">Code partagé · libellés reformulés par audience</p>
                </div>

                {AUDIENCES.map(({ audience, label, dotClass, isCanonical }) => {
                  if (!isCanonical && !(audience === "patient" ? patient : ide)) return null;
                  const audienceQuestion = isCanonical ? question : audience === "patient" ? patientQuestion : ideQuestion;
                  const audienceRequired = isCanonical ? isRequired : audience === "patient" ? patientIsRequired : ideIsRequired;
                  return (
                    <div key={audience} className="overflow-hidden rounded-lg border border-gray-100 bg-white">
                      <div className="flex flex-wrap items-center gap-2.5 border-b border-gray-100 bg-gray-50/40 px-5 py-2.5">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${dotClass}`} />
                        <span className="text-[13px] font-medium text-gray-800">{label}</span>
                        {isCanonical && (
                          <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500 ring-1 ring-gray-200">canonique</span>
                        )}
                        <label className="ml-2 flex cursor-pointer items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-1 focus:ring-primary-500"
                            checked={audienceRequired !== false}
                            onChange={(e) => onChangeAudienceRequired(audience, e.target.checked)}
                          />
                          Obligatoire
                        </label>
                        {audience === "patient" && (
                          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700">
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-1 focus:ring-primary-500"
                              checked={canAnswerInPreRegistration}
                              onChange={(e) => {
                                setCanAnswerInPreRegistration(e.target.checked);
                                markDirty();
                              }}
                            />
                            Réponse en pré-enregistrement
                          </label>
                        )}
                        {!isCanonical && (
                          <button
                            type="button"
                            onClick={() => {
                              if (audience === "patient") {
                                setPatient(false);
                                setPatientQuestion("");
                                setPatientIsRequired(true);
                                setOptions((prev) => prev.map((o) => ({ ...o, patientLabel: "", patientHidden: false })));
                                setCanAnswerInPreRegistration(false);
                              } else {
                                setIde(false);
                                setIdeQuestion("");
                                setIdeIsRequired(true);
                                setOptions((prev) => prev.map((o) => ({ ...o, ideLabel: "", ideHidden: false })));
                              }
                              markDirty();
                            }}
                            className="ml-auto rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                            title={`Supprimer la version ${label}`}
                          >
                            <HiOutlineX className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      <div className="space-y-4 px-5 py-4">
                        <div>
                          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Énoncé</label>
                          <textarea
                            rows={2}
                            className={QUESTION_TEXTAREA_CLASS}
                            placeholder={isCanonical ? "Texte de la question" : question || "Texte de la question"}
                            value={audienceQuestion || ""}
                            onChange={(e) => {
                              if (isCanonical) setQuestion(e.target.value);
                              else if (audience === "patient") setPatientQuestion(e.target.value);
                              else setIdeQuestion(e.target.value);
                            }}
                            disabled={isMutating}
                          />
                        </div>

                        {hasOptions && (
                          <RenditionOptions
                            isCanonical={isCanonical}
                            answerType={answerType}
                            options={options}
                            optionLabels={options.map((o) => ({
                              optionId: o._id,
                              value: isCanonical ? o.label : audience === "patient" ? o.patientLabel : o.ideLabel,
                              disabled: !isCanonical && (audience === "patient" ? o.patientHidden : o.ideHidden),
                            }))}
                            secretaryOptionLabels={options.map((o) => ({ optionId: o._id, value: o.label }))}
                            onReorder={onReorderOptions}
                            onAddOption={onAddOption}
                            onRemoveOption={onRemoveOption}
                            onChangeOptionCode={onChangeOptionCode}
                            onChangeOptionLabel={(optionId, value) => onChangeOptionLabel(audience, optionId, value)}
                            onToggleOptionDisabled={(optionId) => onToggleOptionDisabled(audience, optionId)}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}

                {(!patient || !ide) && (
                  <div className="flex flex-wrap items-center gap-2 px-1 pt-1">
                    <span className="text-xs text-gray-400">Ajouter une version :</span>
                    {AUDIENCES.filter(({ audience }) => (audience === "patient" && !patient) || (audience === "ide" && !ide)).map(({ audience, label, dotClass }) => (
                      <button
                        key={audience}
                        type="button"
                        onClick={() => {
                          if (audience === "patient") {
                            setPatientIsRequired(isRequired);
                            setPatient(true);
                          } else {
                            setIdeIsRequired(isRequired);
                            setIde(true);
                          }
                          markDirty();
                        }}
                        className="flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
                        <HiOutlinePlus className="h-3.5 w-3.5" />
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6 rounded-lg border border-gray-100 bg-white p-5">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="skipIfAlreadyAnswered"
                    className="h-5 w-5 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-2 focus:ring-primary-500"
                    checked={skipIfAlreadyAnswered}
                    onChange={(e) => setSkipIfAlreadyAnswered(e.target.checked)}
                  />
                  <label htmlFor="skipIfAlreadyAnswered" className="ml-3 cursor-pointer text-sm font-medium text-gray-700">
                    Ne pas poser si déjà répondu dans une admission précédente
                  </label>
                </div>
                <p className="ml-8 mt-2 text-xs text-gray-500">
                  Si cette case est cochée, cette question ne sera pas posée si le patient y a déjà répondu dans une admission précédente.
                </p>
                {(answerType === "DATE" || answerType === "HOUR") && (
                  <>
                    <div className="mt-4 flex items-center">
                      <input
                        type="checkbox"
                        id="autoAnswer"
                        className="h-5 w-5 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-2 focus:ring-primary-500"
                        checked={autoAnswer}
                        onChange={(e) => setAutoAnswer(e.target.checked)}
                      />
                      <label htmlFor="autoAnswer" className="ml-3 cursor-pointer text-sm font-medium text-gray-700">
                        Réponse automatique
                      </label>
                    </div>
                    <p className="ml-8 mt-2 text-xs text-gray-500">
                      Si cette case est cochée, la réponse sera automatiquement remplie avec {answerType === "DATE" ? "la date" : "l'heure"} actuelle lors de l'envoi en production
                      de l'admission.
                    </p>
                  </>
                )}
              </div>

              {answerType === "DOCUMENT" && (
                <div className="mt-6 flex gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <HiOutlineInformationCircle className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
                  <p className="text-xs leading-relaxed text-gray-600">
                    Une fois le document fourni, cette question reste active même si ses conditions de déclenchement ne sont plus remplies. Ainsi le document (par exemple la photo
                    de la mutuelle) ne disparaît pas lorsque la secrétaire saisit les informations qu'il contient : il reste disponible pour être intégré au SIL. Pour le retirer,
                    supprimez le document depuis l'admission.
                  </p>
                </div>
              )}

              <div className="mt-6 rounded-lg border border-gray-100 bg-white p-5">
                <Trigger
                  groupsOperator={groupsOperator}
                  setGroupsOperator={(v) => {
                    markDirty();
                    setGroupsOperator(v);
                  }}
                  triggerGroups={triggerGroups}
                  setTriggerGroups={(v) => {
                    markDirty();
                    setTriggerGroups(v);
                  }}
                  codesIncludeRelated
                />
              </div>

              {(linkedActions.length > 0 || linkedGuidelines.length > 0 || linkedQuestions.length > 0) && (
                <div className="mt-8 space-y-6">
                  {linkedActions.length > 0 && (
                    <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                      <div className="mb-3 flex items-center">
                        <LuSquareFunction className="mr-2 h-5 w-5 text-orange-500" />
                        <div className="text-sm font-medium text-orange-700">Actions liées ({linkedActions.length})</div>
                      </div>

                      <div className="space-y-2">
                        {linkedActions.map((action) => (
                          <div
                            key={action._id}
                            className="group cursor-pointer rounded-md border border-orange-100 bg-white p-3 transition-all hover:border-orange-500"
                            onClick={() => window.open(`/action/${action._id}/edit`, "_blank")}
                          >
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-gray-700">{action.name || "—"}</div>
                              <LuArrowRight className="ml-1 shrink-0 text-orange-500 transition-transform group-hover:translate-x-0.5" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {linkedGuidelines.length > 0 && (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                      <div className="mb-3 flex items-center">
                        <LuListTodo className="mr-2 h-5 w-5 text-green-500" />
                        <div className="text-sm font-medium text-green-700">Recommandations liées ({linkedGuidelines.length})</div>
                      </div>

                      <div className="space-y-2">
                        {linkedGuidelines.map((guideline) => (
                          <div
                            key={guideline._id}
                            className="group cursor-pointer rounded-md border border-green-100 bg-white p-3 transition-all hover:border-green-500"
                            onClick={() => window.open(`/guideline/${guideline._id}/edit`, "_blank")}
                          >
                            <div className="flex items-center justify-between">
                              <div className="line-clamp-2 whitespace-pre-wrap text-sm text-gray-700">{guidelineText(guideline)}</div>
                              <LuArrowRight className="ml-1 shrink-0 text-green-500 transition-transform group-hover:translate-x-0.5" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {linkedQuestions.length > 0 && (
                    <div className={`rounded-lg border bg-purple-50 p-4 ${linkedQuestionsInErrors ? "border-red-500" : "border-purple-200"}`}>
                      <div className="mb-3 flex items-center">
                        {linkedQuestionsInErrors ? <HiExclamation className="mr-2 h-5 w-5 text-red-500" /> : <LuMessageCircleQuestion className="mr-2 h-5 w-5 text-purple-500" />}
                        <div className={`text-sm font-medium ${linkedQuestionsInErrors ? "text-red-700" : "text-purple-700"}`}>Questions liées ({linkedQuestions.length})</div>
                      </div>

                      <div className="space-y-2">
                        {linkedQuestions.map((question) => (
                          <div
                            key={question._id}
                            className={`group cursor-pointer rounded-md border bg-white p-3 transition-all ${
                              linkedQuestionsInErrors ? "border-red-100 hover:border-red-500" : "border-purple-100 hover:border-purple-500"
                            }`}
                            onClick={() => window.open(`/question/${question._id}/edit`, "_blank")}
                          >
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-gray-700">{question.question || question._id}</div>
                              <LuArrowRight
                                className={`ml-1 shrink-0 transition-transform group-hover:translate-x-0.5 ${linkedQuestionsInErrors ? "text-red-500" : "text-purple-500"}`}
                              />
                            </div>
                            <div className="mt-2 flex flex-wrap items-start gap-2">
                              <div className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{question.questionCode || question._id}</div>
                              <div className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{audienceBadges(question)}</div>
                              <div className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                                {question.answerType === "MULTIPLE_CHOICE"
                                  ? "Choix multiple"
                                  : question.answerType === "TEXT"
                                    ? "Texte"
                                    : question.answerType === "DATE"
                                      ? "Date"
                                      : question.answerType === "HOUR"
                                        ? "Heure"
                                        : "Document"}
                              </div>
                            </div>

                            {question.triggerGroups &&
                              question.triggerGroups.flatMap((group) => group.triggers).filter((trigger) => trigger.type === "QUESTION" && trigger.field === id).length > 0 && (
                                <div className="mt-3">
                                  <div className="mb-2 text-xs text-gray-500">Réponses liées:</div>
                                  <div className="flex flex-wrap gap-2">
                                    {question.triggerGroups
                                      .flatMap((group) => group.triggers)
                                      .filter((trigger) => trigger.type === "QUESTION" && trigger.field === id)
                                      .flatMap((trigger) => trigger.values)
                                      .map((valueId, idx) => {
                                        const option = options.find((option) => option._id === valueId);
                                        const secretaryLabel = option?.label || null;
                                        return option ? (
                                          <div key={idx} className="rounded-md border border-[#c5e3e6] bg-[#e6f3f4] px-2 py-1 text-xs text-primary-500">
                                            {secretaryLabel || option.code}
                                          </div>
                                        ) : (
                                          <div key={idx} className="flex items-center rounded-md border border-red-100 bg-red-50 px-2 py-1 text-xs text-red-700">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="mr-1 h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                              <path
                                                fillRule="evenodd"
                                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                            Réponse non trouvée
                                          </div>
                                        );
                                      })}
                                  </div>
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </fieldset>
          </div>

          <div className="col-span-2 shrink-0 overflow-y-auto border-l border-gray-200 bg-white p-6">
            {questionData?._sources?.length > 0 && (
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-medium text-gray-900">Synchronisation</h3>
                <SyncInfoBanner sources={questionData?._sources} docId={id} entityType="question" lastSyncChangedFields={questionData?.lastSyncChangedFields} lastSyncChangedAt={questionData?.lastSyncChangedAt} />
              </div>
            )}
            <h3 className="mb-4 text-lg font-medium text-gray-900">Dépendances</h3>
            {diagramData && questionData ? (
              <DependencyExplorer
                data={{
                  questions: [
                    ...diagramData.questions.filter((q) => q._id !== id),
                    { ...questionData, _id: id, question, isRequired, patient, ide, canAnswerInPreRegistration, questionCode, options, triggerGroups, groupsOperator },
                  ],
                  guidelines: diagramData.guidelines,
                  actions: diagramData.actions,
                }}
                currentQuestionId={id}
                className="rounded-lg border border-gray-100"
              />
            ) : (
              <div className="flex items-center justify-center rounded-lg border border-gray-100 py-8 text-sm text-gray-400">Chargement du diagramme...</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EditQuestion;
